import React, { useContext } from 'react';
import {
  GridActionsCellItem,
  GridColDef,
  GridColumnHeaderParams,
  GridPreProcessEditCellProps,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowModes,
  GridValueFormatterParams,
  GridValueSetterParams,
} from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CancelIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@eposnow/ui-core';
import { useTheme } from '@mui/material/styles';
import {
  descriptionValidationSchema,
  nameValidationSchema,
  priceValidationSchema,
} from './validationSchema';
import { SidekickTablesContext } from '../../helpers/SidekickTablesContext';
import GenericGridContent from '../../../../../components/datagridComponents/GenericGridContent';
import GridCellEdit from '../../../../../components/datagridComponents/GridCellEdit';
import GridCellEditTextDropdown from '../../../../../components/datagridComponents/GridCellEditTextDropdown';

const useBuildColumns = (currencySymbol: string): GridColDef[] => {
  const { t } = useTranslation();
  const {
    handleAddClick,
    handleEditClick,
    handleDuplicateClick,
    handleSaveClick,
    handleDeleteClick,
    handleCancelClick,
    rowModesModel,
    categories,
  } = useContext(SidekickTablesContext);
  const theme = useTheme();

  return [
    {
      field: 'name',
      sortable: false,
      editable: true,
      flex: 1,
      minWidth: 150,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) =>
        nameValidationSchema
          .isValid({
            name: params?.props?.value,
          })
          .then((valid) => ({ ...params.props, error: !valid }))
          .catch(() => ({ ...params.props })),
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: t('screens.products.sidekick.import.productsTable.name'),
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <GenericGridContent
          content={[
            {
              text: params.value,
              shouldTranslate: false,
              shouldClipContent: true,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      ),
      renderEditCell: (params: GridRenderEditCellParams) => (
        <GridCellEdit
          gridRenderParams={params}
          labelKey={t('screens.products.sidekick.import.productsTable.name')}
          validationSchema={nameValidationSchema}
        />
      ),
    },
    {
      field: 'description',
      sortable: false,
      minWidth: 175,
      editable: true,
      flex: 1,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) =>
        descriptionValidationSchema
          .isValid({
            description: params?.props?.value,
          })
          .then((valid) => ({ ...params.props, error: !valid }))
          .catch(() => ({ ...params.props })),
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: t('screens.products.sidekick.import.productsTable.description'),
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <GenericGridContent
          content={[
            {
              text: params.value,
              shouldTranslate: false,
              shouldClipContent: true,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      ),
      renderEditCell: (params: GridRenderEditCellParams) => (
        <GridCellEdit
          gridRenderParams={params}
          labelKey={t('screens.products.sidekick.import.productsTable.description')}
          validationSchema={descriptionValidationSchema}
        />
      ),
    },
    {
      field: 'categoryId',
      headerName: 'category',
      editable: true,
      sortable: false,
      flex: 0.5,
      minWidth: 120,
      valueFormatter: (params: GridValueFormatterParams) => {
        const categoryName =
          categories?.find((category) => category.value === params?.value?.toString())?.label ||
          'Top Level';
        return categoryName;
      },
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: t('screens.products.sidekick.import.productsTable.category'),
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      renderCell: (params: GridRenderCellParams) => {
        const categoryName =
          categories?.find((category) => category.value === params?.row?.categoryId?.toString())
            ?.label || 'Top Level';

        return (
          <GenericGridContent
            content={[
              {
                text: categoryName,
                shouldTranslate: false,
                typographyProps: {
                  variant: 'body2',
                },
              },
            ]}
          />
        );
      },
      renderEditCell: (params: GridRenderEditCellParams) => (
        <GridCellEditTextDropdown
          gridRenderParams={params}
          initialChoiceValue={params.row.categoryId}
          options={categories}
          setValueInsteadOfLabel
        />
      ),
    },
    {
      field: 'salePriceIncTax',
      sortable: false,
      editable: true,
      minWidth: 80,
      flex: 0.5,
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) =>
        priceValidationSchema
          .isValid({
            salePriceIncTax: params?.props?.value,
          })
          .then((valid) => ({ ...params.props, error: !valid }))
          .catch(() => ({ ...params.props })),
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: t('screens.products.sidekick.import.productsTable.salePriceIncTax'),
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <GenericGridContent
          content={[
            {
              text: `${currencySymbol || '£'}${params.value}`,
              shouldTranslate: false,
              shouldClipContent: true,
              typographyProps: {
                variant: 'body2',
              },
            },
          ]}
        />
      ),
      valueSetter: (params: GridValueSetterParams) => ({
        ...params.row,
        salePriceIncTax: +params.value,
      }),
      renderEditCell: (params: GridRenderEditCellParams) => (
        <GridCellEdit
          gridRenderParams={params}
          labelKey={t('screens.products.sidekick.import.productsTable.salePriceIncTax')}
          type="number"
          min={0}
          max={10000}
          step={0.01}
          validationSchema={priceValidationSchema}
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      minWidth: 160,
      flex: 0.5,
      renderHeader: (_params: GridColumnHeaderParams) => (
        <GenericGridContent
          content={[
            {
              text: 'common.actions',
              shouldTranslate: true,
              typographyProps: {
                variant: 'boldText',
              },
            },
          ]}
        />
      ),
      align: 'center',
      headerAlign: 'center',
      hideSortIcons: true,
      headerClassName: 'hideRightSeparator',
      getActions: ({ id }) => {
        const newId = id as number;
        const isInEditMode = rowModesModel[newId]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <Tooltip title={t('screens.products.sidekick.import.productsTable.save')} theme={theme}>
              <GridActionsCellItem
                icon={<SaveIcon />}
                label={t('screens.products.sidekick.import.productsTable.save')}
                onClick={() => handleSaveClick(newId)}
                color="inherit"
              />
            </Tooltip>,
            <Tooltip
              title={t('screens.products.sidekick.import.productsTable.cancel')}
              theme={theme}
            >
              <GridActionsCellItem
                icon={<CancelIcon />}
                label={t('screens.products.sidekick.import.productsTable.cancel')}
                onClick={() => handleCancelClick(newId)}
                color="inherit"
              />
            </Tooltip>,
          ];
        }

        return [
          <Tooltip title={t('screens.products.sidekick.import.productsTable.add')} theme={theme}>
            <GridActionsCellItem
              icon={<AddIcon />}
              label={t('screens.products.sidekick.import.productsTable.add')}
              onClick={() => handleAddClick(newId)}
              color="inherit"
            />
          </Tooltip>,
          <Tooltip
            title={t('screens.products.sidekick.import.productsTable.duplicate')}
            theme={theme}
          >
            <GridActionsCellItem
              icon={<ContentCopyIcon />}
              label={t('screens.products.sidekick.import.productsTable.duplicate')}
              onClick={() => handleDuplicateClick(newId)}
              color="inherit"
            />
          </Tooltip>,
          <Tooltip title={t('screens.products.sidekick.import.productsTable.edit')} theme={theme}>
            <GridActionsCellItem
              icon={<EditIcon />}
              label={t('screens.products.sidekick.import.productsTable.edit')}
              onClick={() => handleEditClick(newId)}
              color="inherit"
            />
          </Tooltip>,
          <Tooltip title={t('screens.products.sidekick.import.productsTable.delete')} theme={theme}>
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label={t('screens.products.sidekick.import.productsTable.delete')}
              onClick={() => handleDeleteClick(newId)}
              color="inherit"
            />
          </Tooltip>,
        ];
      },
    },
  ];
};

export default useBuildColumns;
